import * as THREE from "three";
import { MINE_RANGE, MINE_NODE_HITS, mineDurationMs } from "@ghostland/shared";
import { U } from "./streetScene.js";
import { assetUrl } from "../assetVer.js";
import { kaponHeightAt } from "./kaponTerrain.js";
import { drawItemIcon } from "../items/itemArt.js";

/**
 * Client-side mining visuals + the timed mining "channel". Mine NODES are server-authoritative
 * (synced via GameState.mineNodes); this renders each as a pixel boulder. Mining is a timed
 * channel started with E near a node: a pixelated progress bar floats over the player and a
 * pickaxe swings in their hands; when the bar fills, the controller reports the node id so the
 * world sends a Mine message (the server grants the ore + XP). Channel time scales with level.
 */

interface NodeEntity {
  sprite: THREE.Sprite;
  tex: THREE.CanvasTexture;
  x: number; y: number;          // server px
  kind: string;                  // tier (stone/copper/iron/silver/gold) — drives channel duration
  hitsLeft: number;
}

/** Per-kind ore vein colours over a grey boulder. */
const KIND_COLORS: Record<string, [string, string]> = {
  gold: ["#e8b53a", "#f6dd84"],
  silver: ["#b9c2cc", "#eef3f8"],
  copper: ["#c06a38", "#e89a5a"],
  iron: ["#c6a983", "#eaddc6"], // very light brown (distinct from copper's rust)
  stone: ["#7a7a82", "#a2a2aa"],
};

/** Draw a chunky boulder (with kind-coloured veins) into a pixelated canvas. */
function makeNodeTexture(kind: string): THREE.CanvasTexture {
  const W = 40, H = 32;
  const c = document.createElement("canvas"); c.width = W; c.height = H;
  const ctx = c.getContext("2d")!;
  const [vein, gleam] = KIND_COLORS[kind] ?? KIND_COLORS.stone;
  // boulder body
  ctx.fillStyle = "#54545c";
  ctx.fillRect(8, 12, 24, 16); ctx.fillRect(5, 16, 30, 9); ctx.fillRect(12, 9, 16, 3);
  ctx.fillStyle = "#3c3c44"; ctx.fillRect(6, 24, 28, 4); // shadow base
  ctx.fillStyle = "#6c6c76"; ctx.fillRect(11, 11, 12, 2); ctx.fillRect(9, 13, 5, 2); // top light
  // ore veins/flecks
  ctx.fillStyle = vein;
  for (const [vx, vy] of [[14, 17], [22, 15], [26, 20], [17, 22], [12, 19]]) ctx.fillRect(vx, vy, 3, 3);
  ctx.fillStyle = gleam;
  for (const [vx, vy] of [[14, 17], [22, 15], [26, 20]]) ctx.fillRect(vx, vy, 1, 1);
  const tex = new THREE.CanvasTexture(c);
  tex.magFilter = THREE.NearestFilter; tex.minFilter = THREE.NearestFilter; tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Fallback drawn pickaxe (used until the AI-generated sprite loads, or if it's missing). */
function makePickTexture(): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(drawItemIcon("pickaxe"));
  t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter;
  return t;
}

/**
 * A swinging pickaxe billboarded over a miner's head — the shared mining animation. Used both for
 * the LOCAL player (inside MineController, alongside the progress bar) and for REMOTE players (one
 * per mining remote, so everyone sees them chop). Position `group` over the miner each frame and
 * call `swing(nowMs)`; the chop cycle is purely time-based so all viewers see the same motion.
 */
export class MinerPickaxe {
  readonly group = new THREE.Group();
  private readonly pick: THREE.Sprite;

  constructor() {
    // Same depth treatment as the player billboard: alphaTest + depthWrite so buildings OCCLUDE it
    // (it can't be seen through walls) and it writes near depth, keeping it sharp under the world DoF.
    // (depthTest must stay ON — with it off, GL also skips the depth write and the sky behind it blurs it.)
    const pickMat = new THREE.SpriteMaterial({ map: makePickTexture(), transparent: true, alphaTest: 0.5, depthWrite: true });
    this.pick = new THREE.Sprite(pickMat);
    this.pick.scale.set(0.36, 0.36, 1); this.pick.position.set(0, 1.72, 0); // half size, just above the bar
    this.pick.center.set(0.72, 0.16); // pivot at the handle butt (bottom-right) → it chops
    // Upgrade to the AI-generated pickaxe sprite when it loads (no loading-manager dependency).
    const aiImg = new Image();
    aiImg.onload = () => {
      const t = new THREE.Texture(aiImg);
      t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
      t.needsUpdate = true;
      pickMat.map?.dispose(); pickMat.map = t; pickMat.needsUpdate = true;
      this.pick.scale.set(0.36 * (aiImg.width / aiImg.height), 0.36, 1); // keep the sprite's aspect
    };
    aiImg.src = assetUrl("/assets/scene/props/pickaxe.png");
    this.group.add(this.pick);
  }

  /** Advance the chop: a slow anticipation raise, then a fast strike down (loops ~every 0.82s). */
  swing(nowMs: number): void {
    const p = (nowMs / 820) % 1;
    const raise = -1.05, strike = 0.55;
    if (p < 0.62) this.pick.material.rotation = strike + (raise - strike) * (1 - (1 - p / 0.62) ** 2); // ease-out raise
    else { const s = (p - 0.62) / 0.38; this.pick.material.rotation = raise + (strike - raise) * s * s; } // ease-in chop
    this.pick.position.y = 1.72 - (p >= 0.62 ? 0.07 * ((p - 0.62) / 0.38) : 0); // dip on the strike
  }

  dispose(): void {
    const m = this.pick.material as THREE.SpriteMaterial;
    m.map?.dispose(); m.dispose();
  }
}

export class MineController {
  private readonly nodes = new Map<string, NodeEntity>();
  private visible = false;
  private channel: { nodeId: string; start: number; duration: number } | null = null;

  // floating UI: a pixelated progress bar + a swinging pickaxe, parented to a follow group
  private readonly ui = new THREE.Group();
  private readonly barTex: THREE.CanvasTexture;
  private readonly bar: THREE.Sprite;
  private readonly pickFx = new MinerPickaxe();
  private barCanvas: HTMLCanvasElement;

  constructor(private readonly scene: THREE.Scene) {
    this.barCanvas = document.createElement("canvas");
    this.barCanvas.width = 48; this.barCanvas.height = 10;
    this.barTex = new THREE.CanvasTexture(this.barCanvas);
    this.barTex.magFilter = THREE.NearestFilter; this.barTex.minFilter = THREE.NearestFilter;
    this.bar = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.barTex, transparent: true, alphaTest: 0.5, depthWrite: true }));
    this.bar.scale.set(0.5, 0.1, 1); this.bar.position.y = 1.5;        // tight + close over the player (occluded by buildings, like the player)
    this.ui.add(this.bar, this.pickFx.group);
    this.ui.visible = false;
    this.scene.add(this.ui);
  }

  addNode(key: string, node: { kind: string; x: number; y: number; hitsLeft: number }): void {
    const tex = makeNodeTexture(node.kind);
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, alphaTest: 0.4 }));
    sprite.center.set(0.5, 0.04); // stand on the ground
    sprite.position.set(node.x * U, 0.02 + kaponHeightAt(node.x * U, node.y * U), node.y * U);
    sprite.visible = this.visible;
    this.applyScale(sprite, node.hitsLeft);
    this.scene.add(sprite);
    this.nodes.set(key, { sprite, tex, x: node.x, y: node.y, kind: node.kind, hitsLeft: node.hitsLeft });
  }

  updateNode(key: string, hitsLeft: number): void {
    const e = this.nodes.get(key);
    if (!e) return;
    e.hitsLeft = hitsLeft;
    this.applyScale(e.sprite, hitsLeft);
  }

  removeNode(key: string): void {
    const e = this.nodes.get(key);
    if (!e) return;
    this.scene.remove(e.sprite);
    (e.sprite.material as THREE.SpriteMaterial).dispose();
    e.tex.dispose();
    this.nodes.delete(key);
    if (this.channel?.nodeId === key) this.cancel();
  }

  private applyScale(sprite: THREE.Sprite, hitsLeft: number): void {
    const frac = 0.55 + 0.45 * (hitsLeft / MINE_NODE_HITS); // shrinks a little as it depletes
    sprite.scale.set(1.25 * frac, 1.0 * frac, 1);
  }

  /** Show/hide all node sprites for the current zone (mine area lives in the village). */
  setVisible(on: boolean): void {
    if (this.visible === on) return;
    this.visible = on;
    for (const e of this.nodes.values()) e.sprite.visible = on;
    if (!on) this.cancel();
  }

  /** Nearest node within MINE_RANGE of the player (server px), or null. */
  nearest(px: number, py: number): string | null {
    let best: string | null = null, bestD = MINE_RANGE * MINE_RANGE;
    for (const [key, e] of this.nodes) {
      const dx = e.x - px, dy = e.y - py, d = dx * dx + dy * dy;
      if (d <= bestD) { bestD = d; best = key; }
    }
    return best;
  }

  isMining(): boolean { return this.channel != null; }

  /** The tier (stone/copper/iron/silver/gold) of a node, for the level gate. */
  kindOf(key: string): string | null { return this.nodes.get(key)?.kind ?? null; }

  /** Begin a mining channel on a node; duration depends on the node's tier + the pickaxe level. */
  start(key: string, level: number, nowMs: number): void {
    const node = this.nodes.get(key);
    if (!node) return;
    this.channel = { nodeId: key, start: nowMs, duration: mineDurationMs(node.kind as Parameters<typeof mineDurationMs>[0], level) };
    this.ui.visible = true;
  }

  cancel(): void {
    this.channel = null;
    this.ui.visible = false;
  }

  /**
   * Per-frame: keep node sprites billboarded, run the channel (move the UI over the player,
   * fill the bar, swing the pickaxe). Returns the node id to mine when the bar completes
   * (caller sends the Mine message + the channel ends), else null.
   */
  tick(playerX: number, playerZ: number, nowMs: number): string | null {
    if (!this.channel) return null;
    this.ui.position.set(playerX, kaponHeightAt(playerX, playerZ), playerZ);
    const t = Math.min(1, (nowMs - this.channel.start) / this.channel.duration);
    this.drawBar(t);
    this.pickFx.swing(nowMs);
    if (t >= 1) {
      const id = this.channel.nodeId;
      this.cancel();
      return id;
    }
    return null;
  }

  private drawBar(t: number): void {
    const ctx = this.barCanvas.getContext("2d")!;
    const W = this.barCanvas.width, H = this.barCanvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#05000a"; ctx.fillRect(0, 0, W, H);          // backdrop
    ctx.fillStyle = "#2a1b44"; ctx.fillRect(1, 1, W - 2, H - 2);  // empty track
    ctx.fillStyle = "#b58cff"; ctx.fillRect(1, 1, Math.round((W - 2) * t), H - 2); // fill
    ctx.fillStyle = "#1a1024"; // border
    ctx.fillRect(0, 0, W, 1); ctx.fillRect(0, H - 1, W, 1); ctx.fillRect(0, 0, 1, H); ctx.fillRect(W - 1, 0, 1, H);
    this.barTex.needsUpdate = true;
  }
}
